import React, { useState } from 'react';
import Select from 'react-select';
import type { ValueType } from 'react-select';
import Button, { ButtonType } from '../../shared/components/core-ui/button/button.component';
import styles from '../furnishingContainer.module.scss';
import type { PortfolioProject, PortfolioReview } from './portfolio.types';
import { createPortfolioReview } from '../furnishing.service';
import { isErrorInfo } from '../../shared/interfaces/error-info.interface';

interface PortfolioReviewFormProps {
  portfolios: PortfolioProject[];
  onReviewAdded: (portfolioId: string, review: PortfolioReview) => void;
}

interface SelectOption {
  value: string;
  label: string;
}

const generateId = () => `review_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const PortfolioReviewForm: React.FC<PortfolioReviewFormProps> = ({
  portfolios,
  onReviewAdded
}) => {
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
  const [reviewName, setReviewName] = useState('');
  const [reviewDescription, setReviewDescription] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const portfolioOptions: SelectOption[] = portfolios.map((p) => ({
    value: p.id,
    label: `${p.projectName} - ${p.location}`
  }));

  const handlePortfolioChange = (selectedOption: ValueType<SelectOption, false>): void => {
    if (selectedOption) {
      setSelectedPortfolioId((selectedOption as SelectOption).value);
      setError('');
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedPortfolioId) {
      setError('Please select a portfolio first.');
      return;
    }

    if (!reviewName.trim()) {
      setError('Please enter a reviewer name.');
      return;
    }

    if (!reviewDescription.trim()) {
      setError('Please enter a review description.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const result = await createPortfolioReview({
        portfolioId: selectedPortfolioId,
        name: reviewName.trim(),
        description: reviewDescription.trim()
      });

      if (isErrorInfo(result)) {
        setError(result.errorMessage || 'Failed to create review. Please try again.');
        return;
      }

      const portfolioReview: PortfolioReview = {
        id: result.id || generateId(),
        portfolioId: selectedPortfolioId,
        name: result.name || reviewName.trim(),
        description: result.description || reviewDescription.trim()
      };

      onReviewAdded(selectedPortfolioId, portfolioReview);
      resetForm();
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setReviewName('');
    setReviewDescription('');
    setError('');
  };

  const selectedPortfolio = portfolios.find((p) => p.id === selectedPortfolioId);

  if (portfolios.length === 0) return null;

  return (
    <div
      className={styles['furnishing-management__form-card']}
      style={{
        marginTop: '1rem'
      }}
    >
      <div className={styles['furnishing-management__section-header']}>
        <h2>Add Review to Portfolio</h2>
      </div>

      {/* Portfolio Selection */}
      <div style={{ marginBottom: '2rem' }}>
        <div className={styles['furnishing-management__input']}>
          <span>Select Portfolio Project *</span>
          <div className={styles['furnishing-management__select']}>
            <Select
              classNamePrefix="react-select"
              placeholder="Select a portfolio..."
              options={portfolioOptions}
              value={portfolioOptions.find((opt) => opt.value === selectedPortfolioId) || null}
              onChange={handlePortfolioChange}
            />
          </div>
        </div>

        {selectedPortfolio && (
          <div
            style={{
              marginTop: '1rem',
              padding: '1rem',
              background: '#f8f6f1',
              borderRadius: '8px',
              border: '1px solid #e0d8ca'
            }}
          >
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', fontSize: '0.9rem' }}>
              <div>
                <strong>Location:</strong> {selectedPortfolio.location}
              </div>
              <div>
                <strong>Home Type:</strong> {selectedPortfolio.homeType}
              </div>
              <div>
                <strong>Has Review:</strong> {selectedPortfolio.review ? 'Yes' : 'No'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Review Form Section */}
      {selectedPortfolioId && (
        <div className={styles['furnishing-management__models-section']}>
          <div className={styles['furnishing-management__section-header']}>
            <h3>Review Details</h3>
          </div>

          {/* Review Name Input */}
          <div
            className={styles['furnishing-management__form-grid']}
            style={{ marginBottom: '1.5rem' }}
          >
            <div className={styles['furnishing-management__input']}>
              <span>Reviewer Name *</span>
              <input
                type="text"
                placeholder="Enter reviewer name"
                value={reviewName}
                onChange={(e) => {
                  setReviewName(e.target.value);
                  setError('');
                }}
              />
            </div>
          </div>

          {/* Review Description Input */}
          <div
            className={styles['furnishing-management__form-grid']}
            style={{ marginBottom: '1.5rem' }}
          >
            <div className={styles['furnishing-management__input']}>
              <span>Review Description *</span>
              <textarea
                placeholder="Enter review description"
                value={reviewDescription}
                onChange={(e) => {
                  setReviewDescription(e.target.value);
                  setError('');
                }}
                rows={5}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #e0d8ca',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>
        </div>
      )}

      {error && (
        <div
          style={{
            padding: '1rem',
            background: '#ffebee',
            color: '#c62828',
            borderRadius: '8px',
            marginTop: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontWeight: '500'
          }}
        >
          <span style={{ fontSize: '1.2rem' }}>⚠️</span> {error}
        </div>
      )}

      {selectedPortfolioId && (
        <div
          className={styles['furnishing-management__actions']}
          style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #eee' }}
        >
          <Button type={ButtonType.gray} onClick={resetForm} disabled={isSubmitting}>
            Clear Form
          </Button>
          <Button onClick={handleSubmitReview} type={ButtonType.primary} disabled={isSubmitting}>
            {isSubmitting ? 'Adding...' : 'Add Review to Portfolio'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PortfolioReviewForm;
